import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useBottomBoundedViewport from '../utils/useBottomBoundedViewport';
import useBodyScrollLock from '../utils/useBodyScrollLock';
import '../styles/Blog.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const getExcerpt = (blog) => {
  if (blog.excerpt) return blog.excerpt.slice(0, 150);
  if (blog.content) return blog.content.replace(/[#*`>\-[\]()!]/g, '').slice(0, 150).trim() + '…';
  return '';
};

const Blog = ({ lang }) => {
  const bbv = useBottomBoundedViewport({ topOffset: 90, bottomGap: 16 });
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  useBodyScrollLock(!!selectedBlog);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch('/api/blogs');
        const data = await res.json();
        setBlogs(data.blogs || []);
      } catch {
        setBlogs([]);
      }
    };
    fetchBlogs();
  }, []);

  // The blog "modal" is now an inline flowing view (no fixed overlay), so
  // the page must remain scrollable — otherwise the reader cannot scroll
  // down to the rest of the article. Intentionally a no-op; kept here for
  // any future behaviour tied to the modal opening.
  useEffect(() => {
    // no scroll lock — see comment above.
  }, [selectedBlog]);

  // Close modal on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setSelectedBlog(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <section className="blog section" id="blog" ref={ref}>
      <div className="container">
        <motion.div
          className="blog-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="terminal-section-header">
                <div className="terminal-section-titlebar">
                  root@ispclub:~/# cd /ispclub/blog
                </div>
                <div className="terminal-section-body">
                  <span className="terminal-sh-line">&gt;_ sh blog.sh</span>
                </div>
              </div>
          <h2 className="section-title">{lang.BLOG_TITLE || 'Latest Posts'}</h2>
          <p className="section-subtitle">
            {lang.BLOG_SUBTITLE || 'CTF writeups and technical articles.'}
          </p>
        </motion.div>

        <div className="blog-grid">
          {blogs.map((blog, index) => (
            <motion.article
              key={blog.id || index}
              className="blog-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.15 * index, duration: 0.5 }}
              whileHover={{ y: -6 }}
              onClick={() => setSelectedBlog(blog)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setSelectedBlog(blog); }}
            >
              <div className="blog-card-header">
                <span className="blog-category">{blog.category}</span>
                <span className="blog-date">{formatDate(blog.createdAt)}</span>
              </div>
              <h3 className="blog-title">{blog.title}</h3>
              <p className="blog-excerpt">{getExcerpt(blog)}</p>
              <div className="blog-tags">
                {(blog.tags || []).map((tag, tagIndex) => (
                  <span key={tagIndex} className="blog-tag">#{typeof tag === 'string' ? tag.trim() : tag}</span>
                ))}
              </div>
              <div className="blog-footer">
                {/* Author intentionally hidden on the public Blog page —
                    posts are anonymous to all visitors. The real author
                    name is still available inside Dashboard for editing /
                    moderation. */}
                <span className="blog-read-more">
                  {lang.BLOG_READ_MORE || 'Read More'}
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Blog Post Modal */}
        <AnimatePresence>
          {selectedBlog && (
            <motion.div
              className="blog-modal-overlay blog-modal-overlay-bounded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={{ top: bbv.topOffset, bottom: bbv.bottom }}
            >
              <motion.div
                className="blog-modal"
                initial={{ opacity: 0, y: 40, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 40, scale: 0.97 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="blog-modal-close"
                  onClick={() => setSelectedBlog(null)}
                  aria-label={lang.COMMON_CLOSE || 'Close'}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                <div className="blog-modal-header">
                  <span className="blog-category">{selectedBlog.category}</span>
                  <span className="blog-date">{formatDate(selectedBlog.createdAt)}</span>
                </div>

                <h1 className="blog-modal-title">{selectedBlog.title}</h1>

                <div className="blog-modal-meta">
                  {/* Author hidden on public Blog modal too. */}
                  <div className="blog-tags">
                    {(selectedBlog.tags || []).map((tag, i) => (
                      <span key={i} className="blog-tag">#{typeof tag === 'string' ? tag.trim() : tag}</span>
                    ))}
                  </div>
                </div>

                <div className="blog-modal-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {selectedBlog.content || ''}
                  </ReactMarkdown>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Blog;
